import DesignTokens from '@tableau/design-tokens';
import { TabStyles } from '@tableau/widgets';

import { cssClass, fillStyle, flexColumnStyle } from '../../style';

const cssPrefix = 'ContentRelayPlanDetails';

export namespace ContentRelayPlanDetailsStyles {
  // Masthead: icon/title/metadata block grows so Run Now sits right-aligned (matches the mockup).
  export const MastheadRow = cssClass(cssPrefix, 'masthead-row', {
    display: 'flex',
    alignItems: 'flex-start',
  });

  export const MastheadMain = cssClass(cssPrefix, 'masthead-main', {
    position: 'relative',
    flex: '1 1 auto',
    minWidth: 0,
  });

  // No Content Relay masthead SVG exists yet, so the list icon sits centered in the 60px icon slot.
  export const MastheadIcon = cssClass(cssPrefix, 'masthead-icon', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  });

  export const MastheadActions = cssClass(cssPrefix, 'masthead-actions', {
    flexShrink: 0,
    marginLeft: TabStyles.Spacing.MediumSpace,
  });

  export const CenteredState = cssClass(cssPrefix, 'centered-state', fillStyle, flexColumnStyle, {
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    // Margins rather than flex `gap`, which IE11 lacks.
    $nest: { '& > * + *': { marginTop: TabStyles.Spacing.MediumSpace } },
  });

  export const StateTitle = cssClass(cssPrefix, 'state-title', {
    fontWeight: 'bold',
  });

  // Overview tab: toolbar on top, optional alert, then the grid fills the rest and scrolls inside.
  export const OverviewBody = cssClass(cssPrefix, 'overview-body', fillStyle, flexColumnStyle);

  export const Toolbar = cssClass(cssPrefix, 'toolbar', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
    padding: `${TabStyles.Spacing.MediumSpace}px 0`,
    $nest: { '& > * + *': { marginLeft: TabStyles.Spacing.MediumSpace } },
  });

  export const ToolbarCount = cssClass(cssPrefix, 'toolbar-count', {
    color: DesignTokens.color.font.default.secondary,
  });

  export const ToolbarButtons = cssClass(cssPrefix, 'toolbar-buttons', {
    display: 'flex',
    $nest: { '& > * + *': { marginLeft: TabStyles.Spacing.MediumSpace } },
  });

  export const Alert = cssClass(cssPrefix, 'alert', {
    flexShrink: 0,
    marginBottom: TabStyles.Spacing.MediumSpace,
  });

  export const AlertHint = cssClass(cssPrefix, 'alert-hint', {
    display: 'block',
    marginTop: 4,
    color: DesignTokens.color.font.default.secondary,
  });

  export const GridRegion = cssClass(cssPrefix, 'grid-region', {
    position: 'relative',
    flex: '1 1 auto',
    minHeight: 0,
  });

  export const Cell = cssClass(cssPrefix, 'cell', {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    minWidth: 0,
    padding: `0 ${TabStyles.Spacing.MediumSpace}px`,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  });

  export const HeaderCell = cssClass(cssPrefix, 'header-cell', {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    // The sort arrow is the only element child; the header text follows it as a text node.
    $nest: { '& > *': { marginRight: 4 } },
    padding: `0 ${TabStyles.Spacing.MediumSpace}px`,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  });

  export const NameText = cssClass(cssPrefix, 'name-text', {
    flexShrink: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  });

  // Grey note after a name ("Included as related content").
  export const NameNote = cssClass(cssPrefix, 'name-note', {
    flexShrink: 1,
    minWidth: 0,
    marginLeft: TabStyles.Spacing.MediumSpace,
    color: DesignTokens.color.font.default.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  });

  // Distinct flag for a row the server won't download.
  export const MissingNote = cssClass(cssPrefix, 'missing-note', {
    flexShrink: 1,
    minWidth: 0,
    marginLeft: TabStyles.Spacing.MediumSpace,
    color: DesignTokens.color.feedback.default.warning_dark,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  });

  export const Unavailable = cssClass(cssPrefix, 'unavailable', {
    color: DesignTokens.color.font.default.disabled,
    fontStyle: 'italic',
  });

  export const Footer = cssClass(cssPrefix, 'footer', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    $nest: { '& > * + *': { marginLeft: TabStyles.Spacing.MediumSpace } },
    padding: `${TabStyles.Spacing.MediumSpace}px 0`,
  });

  export const ErrorText = cssClass(cssPrefix, 'error-text', {
    color: DesignTokens.color.font.default.error,
  });
}
